import { useEffect, useState } from "react";

/**
 * A React hook that tracks whether an input or textarea element has focus.
 *
 * This hook sets up event listeners on the `focusin` and `focusout` events to
 * track the focus state of input and textarea elements. It returns a boolean
 * value indicating whether an input or textarea currently has focus.
 *
 * @returns {boolean} `true` if an input or textarea element has focus, `false` otherwise.
 */
export function useInputFocus() {
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        setFocused(true);
      }
    };

    const handleBlur = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        setFocused(false);
      }
    };

    document.addEventListener("focusin", handleFocus);
    document.addEventListener("focusout", handleBlur);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("focusout", handleBlur);
    };
  }, []);

  return focused;
}
